import { useMemo } from 'react'
import { LANDING_DESTINATIONS, matchesNavGate } from '@/components/layout/navigation'
import { usePermission } from '@/hooks/usePermission'

/**
 * Where this user's session starts: the FIRST navigation row their role can
 * open, in the sidebar's own order.
 *
 * The portal's home is the Dashboard, but `dashboard.view` is a grant like any
 * other — a role without it used to land on /403 after signing in. It now lands
 * on whatever it can actually see (Customers for most roles, Reports for a
 * reports-only role, and so on), which is the first row of its own rail.
 *
 * `null` means the role can open nothing at all. That is a misconfigured role
 * rather than a state to design for, and the caller sends it to /403.
 *
 * Reads the same destinations and gate rule the sidebar and the topbar menu
 * render from, so the landing screen is always the first row the user can see —
 * including the administration menu, which is where a CMS-only role's one
 * screen lives.
 */
export function useLandingPath(): string | null {
  const { can, canViewModule, isSuperadmin } = usePermission()

  return useMemo(
    () =>
      LANDING_DESTINATIONS.find((destination) =>
        matchesNavGate(destination, { can, canViewModule, isSuperadmin }),
      )?.to ?? null,
    [can, canViewModule, isSuperadmin],
  )
}
